import { useAtomValue } from "jotai";
import { useMemo } from "react";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import { questionnaireCurrentSectionAtom } from "../state/questionnaireCurrentSectionAtom";
import { questionnaireCombinedErrorsAtom } from "../state/questionnaireCombinedErrorsAtom";

/**
 * Get current section state + navigation info. Only re-renders on section navigation.
 *
 * @returns { currentSection, currentSectionIndex, lastViewedSectionIndex, isFirstSection, isLastSection, totalSections, errors }
 * @example const { currentSection, isLastSection, errors } = useCurrentSection();
 * @see useSections - all sections (re-renders on any change)
 */
export function useCurrentSection(initialSections?: SectionProps[]) {
  const sectionsData = useAtomValue(questionnaireCurrentSectionAtom);
  const errors = useAtomValue(questionnaireCombinedErrorsAtom);

  // Memoize section errors based on current section and errors
  const currentSection =
    sectionsData?.currentSection ||
    initialSections?.[sectionsData?.currentSectionIndex ?? 0];

  const sectionErrors = useMemo(() => {
    if (!currentSection || !errors || Object.keys(errors).length === 0) {
      return {};
    }

    const currentSectionQuestionNames = currentSection.questions
      .filter(q => "name" in q)
      .map(q => (q as { name: string }).name);

    return Object.keys(errors)
      .filter(errorKey => currentSectionQuestionNames.includes(errorKey))
      .reduce((acc, key) => ({ ...acc, [key]: errors[key] }), {});
  }, [errors, currentSection]);

  if (!sectionsData) {
    return {
      currentSection: initialSections?.[0],
      currentSectionIndex: 0,
      lastViewedSectionIndex: 0,
      isFirstSection: true,
      isLastSection: false,
      totalSections: initialSections?.length ?? 0,
      errors: sectionErrors,
    };
  }

  if (sectionsData.currentSection) {
    return {
      ...sectionsData,
      errors: sectionErrors,
    };
  }

  // This is mostly for tests, where the sections haven't yet been set
  return {
    ...sectionsData,
    currentSection,
    errors: sectionErrors,
  };
}
